/** Resources */
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
/** Components */
import { CenterFrameLayout } from 'Layouts';
import { PlanPreview } from 'Features/Plans/PlanPreview';
import { PromoCodeForm } from 'Forms/PromoCodeForm/PromoCodeForm';
import { PaymentTotalDue } from 'Features/Payment/PaymentTotalDue/PaymentTotalDue';
import { SubmitError } from 'Components/Fields/SubmitError';
import { PaymentFinePrint } from 'Features/Payment/PaymentFinePrint';
import { PaymentMethodSelector } from 'Features/PaymentMethodSelector/PaymentMethodSelector';
import { CardPayment } from 'Features/PaymentMethodSelector/CardPayment';
import { PaypalPayment } from 'Features/PaymentMethodSelector/PaypalPayment';
import { SubmitPaymentButton } from 'Features/Payment/SubmitPaymentButton/SubmitPaymentButton';
/** Helpers */
import { trackPaymentStarted } from 'Utils/PaymentMethods';
import { isSheerIdFreePlan } from 'Utils/SheerIdHelpers';
import { isFreeMonthPromoPlanActive, isMonthlyPromoPlanActive } from 'Utils/Plans';
import { PAYMENT_METHODS } from 'Features/PaymentMethodSelector/PaymentMethodSelector';
import { trackPaymentAction } from 'Utils/PaymentMethods';
import { usePayPal } from 'Hooks/usePayPal';
import { logger } from 'Utils/Logger';
import useVisualViewport from 'Hooks/useVisibleViewport';
import { fetchPaymentMethods, payment } from 'State/PaymentMethods';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { createPaymentAccount, setPaypalError } from 'State/Checkout';
import { FloatingBottomFooter } from 'Components/FloatingBottomFooter/FloatingBottomFooter';
import { getPlans, selectPlan } from 'State/Plans';
import { MANAGE_PLAN_PURCHASE_MODE, VA_QUERY_PARAM } from 'Utils/Constants';
import { useParams } from 'react-router-dom';
import { setPurchaseMode } from 'State/PurchaseMode';
import { isVAFreePlan } from 'Utils/VeteransAdvHelpers';
import { useStore } from 'Hooks/useStore';
import { sessionStorageSet } from 'Utils/Storage';
import { getUserSheerInfo } from 'State/Integrations';
import './PaymentPage.css';

export const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setLoading] = useState(true);
  const [selectedPaymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CARD);
  const [isSheerIdFree, setIsSheerIdFree] = useState(false);
  const locale = useStore('appConfig.strings.payment');
  const { sheerIdTitle, sheerIdSubtitle } = locale;
  const routes = useStore('appConfig.routes');
  const purchaseMode = useStore('purchaseMode');
  const promoCode = useStore('promoCode');
  const checkout = useStore('checkout');
  const paymentMethods = useStore('paymentMethods');
  const subscription = useStore('subscription');
  const plans = useStore('plans');
  const selectedPlanId = useStore('plans.selectedPlanId');
  const { member_id, hasVerified, error: vaError } = useStore('integrations.veteransAdv');
  const isVaUser = Boolean(member_id || hasVerified);
  const { program } = useStore('integrations.sheerId');
  const { preSelectedPlan = '' } = useParams();
  const [enablePaymentButton, setEnablePaymentButton] = useState(false);
  const [isUpdateCardMode, setIsUpdateCardMode] = useState(false);
  const [submitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSheerVerified, setIsSheerVerified] = useState(false);
  /* only true if saved credit card form and vermont checkbox is present/visible yet not checked */
  const [vtCheckMissing, setVtCheckMissing] = useState(false);
  const { allPlans, isFetchingPlans, getPlansSuccess, isPlanSelected, selectedPlan, selectPlanActionComplete } = plans;
  const selectedPlanStrings = allPlans.find((plan) => plan.planId === selectedPlanId);
  const { shouldHavePaymentAccount } = checkout;
  const { isChurnedUser, hasSubscription, purchaseType, isSuccess, isStatusSuccess, isChangePlanAvailable } =
    subscription;
  const [isPayPalEnabled] = usePayPal();
  const { hasApplePayPayment, hasCardPayment, hasPaypalPayment } = paymentMethods;
  const isMonthlyPromoActive = isPlanSelected && isMonthlyPromoPlanActive(selectedPlan);
  const isFreeMonthPromoActive = isPlanSelected && isFreeMonthPromoPlanActive(selectedPlan);
  const centerFrameSubtitle = isSheerIdFree && !isSheerVerified ? sheerIdSubtitle : '';
  const savedTypes = {
    [PAYMENT_METHODS.CARD]: hasCardPayment,
    [PAYMENT_METHODS.PAYPAL]: hasPaypalPayment,
    [PAYMENT_METHODS.APPLE]: hasApplePayPayment,
  };
  const hasSavedPayment = savedTypes[selectedPaymentMethod];
  const fromSheerVerification = location.state?.from === '/account/verification';
  // shows successful verification banner only if user comes from verification page & is verified
  const shouldShowSuccessBanner = (fromSheerVerification && isSheerVerified) || isVAFreePlan(selectedPlan?.planId);

  const urlParams = new URLSearchParams(location.search);
  const isVeteransAdv = urlParams.get(VA_QUERY_PARAM);

  // begin forwardRefs to submit payment forms
  const cardPaymentForm = useRef();
  const savedCardPaymentForm = useRef();
  const savedPaypalForm = useRef();
  // end forwardRefs
  const { isObstructed } = useVisualViewport();

  // Functionality for submit button in parent with forms in children components
  const submitPaymentForm = useCallback(
    (paymentMethod) => {
      if (paymentMethod === PAYMENT_METHODS.CARD && !hasCardPayment) {
        cardPaymentForm.current.submitForm();
      }
      if (paymentMethod === PAYMENT_METHODS.CARD && hasCardPayment) {
        //If Updating Card, the ref will switch back to cardPaymentForm
        if (savedCardPaymentForm.current === null) {
          cardPaymentForm.current.submitForm();
        } else {
          savedCardPaymentForm.current.submitForm();
        }
      }
      if (paymentMethod === PAYMENT_METHODS.PAYPAL && hasPaypalPayment) {
        savedPaypalForm.current.submitForm();
      }
    },
    [hasCardPayment, hasPaypalPayment, savedPaypalForm, savedCardPaymentForm, cardPaymentForm]
  );

  const handlePayment = useCallback(
    async (type, data = {}, trackPaymentMethod) => {
      logger.started('handle payment', { type, data });

      return payment(type, data, trackPaymentMethod)
        .then(() => {
          const confirmationURL = routes.signupConfirmation;
          logger.completed('handle payment', {
            redirectUrl: confirmationURL,
          });
          setErrorMessage('');

          // This data should be on session storage on the payment complete page for the Heap integration
          // https://teamfox.atlassian.net/browse/NATCOM-1511
          sessionStorageSet('purchased_plan_id', selectedPlanId);

          navigate(confirmationURL);
        })
        .catch((error) => {
          logger.error('handle payment', { type, data }, error);
          if (type === 'apple pay') {
            setErrorMessage(
              'Something went wrong, please check your Apple device and payment information and try again'
            );
          } else {
            setErrorMessage(error.message);
          }
          setIsSubmitting(false);
          return type === 'paypal' ? setPaypalError(error.message) : onSubmitReactFormError(error.message);
        });
    },
    [routes, navigate, selectedPlanId]
  );

  const handleCardPayment = () => {
    handlePayment('card', { paymentmethodInfo: { label: 'Card' } }, PAYMENT_METHODS.CARD);
    setErrorMessage('');
  };

  const handleSavedPayment = (paymentmethodInfo, trackPaymentMethod) => {
    handlePayment('saved', { paymentmethodInfo }, trackPaymentMethod);
  };

  const getCenterFrameTitle = () => {
    let centerFrameTitle = 'Add Your Payment Method';
    if (isSheerIdFree && !isSheerVerified) {
      centerFrameTitle = sheerIdTitle;
    }
    if (isMonthlyPromoActive) {
      centerFrameTitle = 'Start Watching Now';
    }
    if (isFreeMonthPromoActive) {
      centerFrameTitle = '30-day free trial - Start watching now.';
    }
    if (isChurnedUser) {
      centerFrameTitle = 'Start Watching Now';
    }
    if (subscription.hasActiveSubscription) {
      centerFrameTitle = 'Submit Payment';
    }
    return centerFrameTitle;
  };

  const submitButtonText = useMemo(() => {
    let buttonText = 'Start Subscription';
    if (isMonthlyPromoActive) {
      buttonText = 'Join Now';
    }
    if (subscription.hasActiveSubscription || isSheerIdFree) buttonText = 'Submit';
    if (selectedPaymentMethod === 'paypal') buttonText = 'Continue with PayPal';
    if (selectedPaymentMethod === 'apple pay') buttonText = 'Buy with Pay';
    return buttonText;
  }, [isSheerIdFree, isMonthlyPromoActive, subscription, selectedPaymentMethod]);

  const paymentCustomClass = `${centerFrameSubtitle ? 'paymentFlowWithSub' : null} paymentPage`;

  /** Begin useEffects */

  useEffect(() => {
    if (!isLoading) trackPaymentStarted();
  }, [isLoading]);

  // Ensure that plans have actually been loaded on this screen instead of receiving state
  // from previous screens
  const [hasLoadedPlans, setHasLoadedPlans] = useState(false);
  useEffect(() => {
    //Needed since sometimes other pages redirect to this page with a preSelectedPlan added in url
    if (isSuccess && isStatusSuccess) {
      getPlans(preSelectedPlan);
      setHasLoadedPlans(true);
    }
  }, [preSelectedPlan, isSuccess, isStatusSuccess]);

  useEffect(() => {
    if (!shouldHavePaymentAccount) {
      createPaymentAccount();
    }
  }, [shouldHavePaymentAccount]);

  useEffect(() => {
    if (shouldHavePaymentAccount && !paymentMethods.isSuccess && !paymentMethods.isFetching) {
      fetchPaymentMethods();
    }
  }, [paymentMethods, shouldHavePaymentAccount]);

  useEffect(() => {
    if (
      shouldHavePaymentAccount &&
      paymentMethods.isSuccess &&
      !(preSelectedPlan && !getPlansSuccess && isFetchingPlans) &&
      selectedPlan
    ) {
      setLoading(false);
    }
  }, [paymentMethods, shouldHavePaymentAccount, getPlansSuccess, isFetchingPlans, preSelectedPlan, selectedPlan]);

  // set manage purchaseMode for existing active subscription users.
  useEffect(() => {
    if (isSuccess && hasSubscription && isChangePlanAvailable) {
      setPurchaseMode(MANAGE_PLAN_PURCHASE_MODE);
    }
  }, [isSuccess, hasSubscription, isChangePlanAvailable]);

  useEffect(() => {
    const getSheerVerification = async () => {
      try {
        const memberStatus = await getUserSheerInfo();
        const { currentStep } = memberStatus;
        setIsSheerVerified(currentStep === 'success');
      } catch (e) {
        setIsSheerVerified(false);
      }
      setIsSheerIdFree(isSheerIdFreePlan(!!selectedPlan && selectedPlan.packageID));
    };

    getSheerVerification();
  }, [selectedPlan]);

  useEffect(() => {
    if (vaError) {
      navigate(routes.vetAdvantageError);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, routes.vetAdvantageError, vaError]);

  /** End useEffects */

  const isFreePlan = isSheerIdFree || isVAFreePlan(selectedPlan?.planId);

  if (
    selectPlanActionComplete &&
    !isPlanSelected &&
    !isFetchingPlans &&
    hasLoadedPlans &&
    !plans.willFetchVeteransAdvAfterSignup
  ) {
    return <Navigate replace to={routes.signupPlans} />;
  }

  // if user that signed up from Nation app & has subscription tries to check out
  // we're redirecting them to current subscriber page
  // if Veterans Advantage/WeSalute, don't redirect here because we want to redirect to VA error page instead
  if (
    hasSubscription &&
    !!purchaseType &&
    purchaseType !== 'web' &&
    purchaseType !== 'coupon' &&
    purchaseType !== 'gift' &&
    !isVeteransAdv
  ) {
    return <Navigate replace to={{ search: location.search, pathname: routes.signupCurrentSubscriber }} />;
  }

  return (
    <CenterFrameLayout
      title={getCenterFrameTitle()}
      subtitle={centerFrameSubtitle}
      showLoader={isLoading}
      customClass={paymentCustomClass}
      status={shouldShowSuccessBanner && 'Success! You’ve been verified.'}
      statusType="success"
      modernLayout
    >
      <PlanPreview
        plan={selectedPlanStrings}
        isFreePlan={isFreePlan}
        subscription={subscription}
        promoMsg={promoCode.hasApplied && promoCode.promoDescription}
        allPlans={allPlans}
        selectPlan={selectPlan}
      />
      <PromoCodeForm isFreePlan={isFreePlan} submitButtonText="Apply" addWrapper={true} canAutoApplyCoupon={true} />
      <PaymentMethodSelector
        selectedPaymentMethod={selectedPaymentMethod}
        setPaymentMethod={setPaymentMethod}
        setErrorMessage={setErrorMessage}
      />
      {selectedPaymentMethod === PAYMENT_METHODS.CARD && (
        <CardPayment
          handleCardPayment={(data) => handleCardPayment(data, PAYMENT_METHODS.CARD)}
          handleSavedPayment={(data) => handleSavedPayment(data, PAYMENT_METHODS.CARD)}
          submitButtonText={submitButtonText}
          trackPaymentAction={trackPaymentAction}
          cardPaymentForm={cardPaymentForm}
          setEnablePaymentButton={setEnablePaymentButton}
          savedCardPaymentForm={savedCardPaymentForm}
          setIsSubmitting={setIsSubmitting}
          hasSavedPayment={hasSavedPayment}
          setIsUpdateCardMode={setIsUpdateCardMode}
          setVtCheckMissing={setVtCheckMissing}
          setErrorMessage={setErrorMessage}
        />
      )}
      {isPayPalEnabled && selectedPaymentMethod === PAYMENT_METHODS.PAYPAL && paymentMethods.hasPaypalPayment && (
        <PaypalPayment
          handleSavedPayment={(data) => handleSavedPayment(data, PAYMENT_METHODS.PAYPAL)}
          submitButtonText={submitButtonText}
          savedPaypalForm={savedPaypalForm}
        />
      )}
      {!paymentMethods.hasPaypalPayment && !selectedPaymentMethod === PAYMENT_METHODS.PAYPAL && (
        <hr className="sectionLine bottom" />
      )}
      <PaymentTotalDue />
      <SubmitError error={errorMessage} trackError={false} />
      <SubmitPaymentButton
        paymentType={selectedPaymentMethod}
        hasSavedPayment={hasSavedPayment}
        handlePayment={handlePayment}
        submitPaymentForm={submitPaymentForm}
        submitButtonText={submitButtonText}
        submitting={submitting}
        setIsSubmitting={setIsSubmitting}
        isObstructed={isObstructed}
        isDisabled={!enablePaymentButton && (!hasSavedPayment || isUpdateCardMode || vtCheckMissing)}
        trackPaymentAction={trackPaymentAction}
      />
      <PaymentFinePrint
        purchaseMode={purchaseMode}
        ctaText={submitButtonText}
        sheerIdProgram={program}
        isVaUser={isVaUser}
        selectedPlanId={selectedPlanId}
      />
      <FloatingBottomFooter
        component={
          <SubmitPaymentButton
            paymentType={selectedPaymentMethod}
            hasSavedPayment={hasSavedPayment}
            handlePayment={handlePayment}
            submitPaymentForm={submitPaymentForm}
            submitButtonText={submitButtonText}
            submitting={submitting}
            setIsSubmitting={setIsSubmitting}
            isObstructed={isObstructed}
            isDisabled={!enablePaymentButton && (!hasSavedPayment || isUpdateCardMode || vtCheckMissing)}
            trackPaymentAction={trackPaymentAction}
            className={'floatingSubmitMobileOnly'}
          />
        }
        mobileOnly={true}
      />
    </CenterFrameLayout>
  );
};
